import ArticleLayout from '@/components/feature/ArticleLayout';
import { useLang } from '@/hooks/useLang';

export default function AllChinaBuyReviewPage() {
  const currentLang = useLang();

  return (
    <ArticleLayout
      title="AllChinaBuy Review 2026 — Pros, Cons & What to Know"
      metaDesc="Independent AllChinaBuy review covering purchasing experience, shipping, warehouse processes, seller research, QC photos, and common risks. Updated for 2026."
      h1="AllChinaBuy Review 2026 — What You Need to Know"
      author="Editorial Team"
      publishedDate="2026-02-01"
      updatedDate="2026-08-26"
      breadcrumbs={[
        { label: 'Home', href: `/${currentLang}/` },
        { label: 'Review', href: `/${currentLang}/allchinabuy-review/` },
      ]}
    >
      <div className="prose max-w-none">
        {/* Direct Answer */}
        <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 mb-10">
          <p className="text-lg text-foreground-800 leading-relaxed m-0">
            <strong>AllChinaBuy</strong> is a buying-agent platform that helps international buyers purchase products from Chinese marketplaces. Our independent review examines the purchasing process, shipping options, warehouse services, QC practices, and potential risks to help you make an informed decision.
          </p>
        </div>

        {/* Table of Contents */}
        <h2 className="font-heading text-2xl font-semibold text-foreground-950 mt-12 mb-4">Table of Contents</h2>
        <nav className="bg-background-100 border border-background-200/70 rounded-lg p-5 mb-10">
          <ul className="space-y-2 text-sm">
            <li><a href="#overview" className="text-primary-600 hover:text-primary-700">Overview of AllChinaBuy</a></li>
            <li><a href="#purchasing" className="text-primary-600 hover:text-primary-700">Purchasing Process</a></li>
            <li><a href="#warehouse" className="text-primary-600 hover:text-primary-700">Warehouse & Storage</a></li>
            <li><a href="#qc" className="text-primary-600 hover:text-primary-700">QC Photos & Quality</a></li>
            <li><a href="#shipping" className="text-primary-600 hover:text-primary-700">Shipping Experience</a></li>
            <li><a href="#pros-cons" className="text-primary-600 hover:text-primary-700">Pros & Cons</a></li>
            <li><a href="#alternatives" className="text-primary-600 hover:text-primary-700">Alternatives Comparison</a></li>
            <li><a href="#safety" className="text-primary-600 hover:text-primary-700">Safety & Legitimacy</a></li>
            <li><a href="#faq" className="text-primary-600 hover:text-primary-700">FAQ</a></li>
          </ul>
        </nav>

        {/* Overview */}
        <h2 id="overview" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Overview of AllChinaBuy</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          AllChinaBuy operates as a buying-agent platform, serving as an intermediary between international buyers and Chinese e-commerce marketplaces. The platform handles product purchasing, warehouse storage, quality checks, and international shipping on behalf of buyers.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-4">
          For buyers who cannot directly purchase from Chinese marketplaces due to language barriers, payment limitations, or shipping restrictions, buying-agent platforms like AllChinaBuy provide a structured solution. However, it is important to understand that these platforms are intermediaries — they do not manufacture products and cannot guarantee seller behavior or product quality.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-6">
          To explore available products, check out the <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 font-medium">AllChinaBuy Spreadsheet</a> on our main website.
        </p>

        {/* Purchasing Process */}
        <h2 id="purchasing" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Purchasing Process</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          The typical purchasing process through AllChinaBuy involves several steps. Understanding each stage can help you set appropriate expectations and plan your purchases accordingly:
        </p>
        <ol className="list-decimal list-inside space-y-3 text-foreground-700 mb-6 pl-1">
          <li><strong>Product selection</strong> — Browse or search for products using links from the seller or spreadsheet.</li>
          <li><strong>Order submission</strong> — Submit the product link through the platform for purchase.</li>
          <li><strong>Payment</strong> — Pay for the product plus any applicable service fees.</li>
          <li><strong>Procurement</strong> — The platform purchases the item from the seller on your behalf.</li>
          <li><strong>Arrival at warehouse</strong> — The item arrives at the platform\'s warehouse for inspection.</li>
          <li><strong>QC inspection</strong> — Quality check photos are taken and made available for review.</li>
          <li><strong>Shipping selection</strong> — Choose your preferred shipping method to your destination.</li>
          <li><strong>Delivery</strong> — The package is shipped and delivered to your address.</li>
        </ol>

        {/* Warehouse */}
        <h2 id="warehouse" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Warehouse & Storage</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Warehouse services are a core component of the buying-agent experience. Items purchased on your behalf are stored at the platform\'s warehouse until you approve shipping. This storage period allows time for quality checks and provides the opportunity to consolidate multiple items into a single shipment.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Key warehouse considerations include storage duration limits, consolidation options, packaging choices, and re-packaging services. Some platforms offer free storage for a limited period, while extended storage may incur fees.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-6">
          Learn more in our <a href="/${currentLang}/allchinabuy-warehouse/" className="text-primary-600 hover:text-primary-700 font-medium">Warehouse Guide</a>.
        </p>

        {/* QC */}
        <h2 id="qc" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">QC Photos & Quality</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Quality Check (QC) photos are one of the most valuable features of buying-agent platforms. These photos show the actual product received at the warehouse, allowing you to verify condition, accuracy, and quality before committing to international shipping.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-4">
          When reviewing QC photos, pay attention to stitching quality, material texture, color accuracy, logo placement, and packaging condition. If the product does not meet your expectations, you can typically request an exchange or return before shipping, though policies vary by platform.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-6">
          For detailed QC guidance, see our <a href="/${currentLang}/allchinabuy-legit/" className="text-primary-600 hover:text-primary-700 font-medium">Safety & Legitimacy Guide</a>.
        </p>

        {/* Shipping */}
        <h2 id="shipping" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Shipping Experience</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Shipping is a critical factor in the overall buying experience. AllChinaBuy typically offers multiple shipping methods ranging from economical options to express delivery. Factors affecting shipping include destination country, parcel weight and dimensions, chosen carrier, and any applicable customs duties or taxes.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-4">
          Delivery time estimates vary significantly based on the shipping method selected. Economy shipping may take several weeks, while express options can reduce delivery time substantially at a higher cost. Always factor in potential customs processing delays when estimating delivery dates.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-6">
          Read our complete <a href="/${currentLang}/allchinabuy-shipping/" className="text-primary-600 hover:text-primary-700 font-medium">Shipping Guide</a> for more information.
        </p>

        {/* Pros & Cons */}
        <h2 id="pros-cons" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Pros & Cons</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-accent-50 border border-accent-200/70 rounded-lg p-5">
            <h3 className="font-semibold text-accent-800 mb-3 flex items-center gap-2">
              <i className="ri-check-line" /> Pros
            </h3>
            <ul className="space-y-2 text-sm text-foreground-700">
              <li>Access to products from Chinese marketplaces</li>
              <li>QC photos before shipping</li>
              <li>Parcel consolidation options</li>
              <li>Multiple shipping methods available</li>
              <li>Structured buying process</li>
              <li>English-language support interface</li>
            </ul>
          </div>
          <div className="bg-secondary-50 border border-secondary-200/70 rounded-lg p-5">
            <h3 className="font-semibold text-secondary-800 mb-3 flex items-center gap-2">
              <i className="ri-close-line" /> Cons
            </h3>
            <ul className="space-y-2 text-sm text-foreground-700">
              <li>Additional service fees on top of product cost</li>
              <li>Shipping can be expensive for small orders</li>
              <li>Delivery times vary and can be unpredictable</li>
              <li>Quality depends on original seller, not the platform</li>
              <li>Return/exchange process can be complex</li>
              <li>Customs duties may apply</li>
            </ul>
          </div>
        </div>

        {/* Alternatives */}
        <h2 id="alternatives" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Alternatives Comparison</h2>
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm border border-background-200/70 rounded-lg overflow-hidden">
            <thead className="bg-background-100">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-foreground-800">Feature</th>
                <th className="px-4 py-3 text-center font-semibold text-foreground-800">AllChinaBuy</th>
                <th className="px-4 py-3 text-center font-semibold text-foreground-800">Other Agent A</th>
                <th className="px-4 py-3 text-center font-semibold text-foreground-800">Other Agent B</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/70">
              {[
                ['QC Photos', '✓', '✓', '✓'],
                ['Parcel Consolidation', '✓', '✓', '—'],
                ['Multiple Shipping Options', '✓', '✓', '✓'],
                ['English Interface', '✓', '✓', 'Partial'],
                ['Live Chat Support', 'Partial', '✓', '—'],
                ['Mobile App', '—', '✓', '✓'],
                ['Insurance Options', '✓', '✓', 'Partial'],
              ].map(([feature, ...vals]) => (
                <tr key={feature} className="bg-background-50">
                  <td className="px-4 py-3 font-medium text-foreground-800">{feature}</td>
                  {vals.map((v, i) => (
                    <td key={i} className="px-4 py-3 text-center text-foreground-600">{v}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm text-foreground-500 mb-6">
          Note: This comparison is based on general features commonly available. Specific capabilities and pricing vary over time. Always verify current information directly with each platform.
        </p>

        {/* Safety */}
        <h2 id="safety" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Safety & Legitimacy</h2>
        <p className="text-foreground-700 leading-relaxed mb-4">
          AllChinaBuy operates as a legitimate business entity providing buying-agent services. However, legitimacy of the platform does not guarantee the quality of individual sellers, products, or transactions. The platform serves as an intermediary, and the actual product quality depends on the original marketplace seller.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-4">
          To minimize risks, always review QC photos before approving shipping, research sellers independently, use secure payment methods, and understand the platform\'s dispute resolution process. Be aware of potential counterfeit products and familiarize yourself with your country\'s import regulations.
        </p>
        <p className="text-foreground-700 leading-relaxed mb-6">
          For comprehensive safety information, read our <a href="/${currentLang}/allchinabuy-legit/" className="text-primary-600 hover:text-primary-700 font-medium">Safety & Legitimacy Guide</a>.
        </p>

        {/* FAQ */}
        <h2 id="faq" className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">Frequently Asked Questions</h2>
        <div className="space-y-4 mb-10">
          {[
            {
              q: 'Is AllChinaBuy safe to use?',
              a: 'AllChinaBuy operates as a registered business. However, safety depends on multiple factors including seller reliability, payment security, and your own research practices. Follow our safety guidelines for best practices.',
            },
            {
              q: 'How long does shipping take?',
              a: 'Shipping times vary by destination and method. Economy shipping typically takes 2-6 weeks, while express options can deliver within 1-2 weeks. Customs processing may add additional time.',
            },
            {
              q: 'Can I return products if I am not satisfied?',
              a: 'Return policies depend on the specific platform and seller. Some items can be returned to the warehouse before international shipping. After shipping, returns become more complex and may not be feasible.',
            },
            {
              q: 'Are there any hidden fees?',
              a: 'Common fees include product cost, service fee, shipping fee, and optional insurance. Customs duties may apply depending on your destination country. Always review the full cost breakdown before confirming orders.',
            },
            {
              q: 'How do I contact AllChinaBuy?',
              a: 'Most platforms offer customer support through their website interface, email, or messaging systems. Response times vary. Check the platform\'s contact page for current support options.',
            },
          ].map((faq, i) => (
            <div key={i} className="bg-background-100 border border-background-200/70 rounded-lg p-5">
              <h3 className="font-semibold text-foreground-900 mb-2">{faq.q}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 md:p-8 text-center">
          <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">
            Explore the AllChinaBuy Spreadsheet
          </h3>
          <p className="text-sm text-foreground-600 mb-5">
            Find products, sellers, and buying resources organized in one place.
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-colors"
          >
            Visit Main Website →
          </a>
        </div>

        {/* Disclaimer */}
        <div className="mt-10 pt-6 border-t border-background-200/70">
          <p className="text-xs text-foreground-500 leading-relaxed">
            <strong>Disclaimer:</strong> This review is based on independent research and publicly available information. It does not constitute an endorsement or guarantee of any platform, seller, or transaction outcome. Always conduct your own research before making purchases. Features, pricing, and policies may change over time.
          </p>
        </div>
      </div>
    </ArticleLayout>
  );
}